// import { encode } from "@msgpack/msgpack";
import { connectionSelector, setMraMessage } from "@poscon/shared-frontend";
import { typedSplitString } from "@poscon/shared-types";
import { invoke } from "@tauri-apps/api/core";
import { env } from "~/env";
import { RootStore } from "~/redux/store";

const TRANSCRIBE_TIMEOUT = 15000;

type TranscribeData = {
    audio_file_path?: string | null;
    callsign?: string | null;
    text?: string;
} & Record<string, unknown>;

function shouldIgnoreResponsePlayback(instr: object) {
    return Object.keys(instr).some((k) => k.startsWith("say"))
}

const data: { chunks: Blob[]; isRecording: boolean; transcribeVoiceBaseUrl: string | null; store: RootStore | null } = {
    isRecording: false,
    chunks: [],
    transcribeVoiceBaseUrl: null,
    store: null,
};

export const voiceUrls: Record<string, string> = {
    EU: "https://services.poscon.com/voice-eu",
    // US: "https://services.poscon.com/voice-us",
    LOCAL8000: "http://localhost:8000/voice",
};

export function setVoiceTranscribeURL(url: string) {
    data.transcribeVoiceBaseUrl = url;
    console.log(`voice transcribe URL set to: ${url}`);
}

const ERAM_SERVER_URL = env.VITE_ERAM_SERVER_URL;

export function injectAudioStore(s: RootStore) {
    data.store = s;
}

async function createRecorder() {
    try {
        return await navigator?.mediaDevices
            .getUserMedia({
                audio: true,
            })
            .then((stream) => new MediaRecorder(stream, { mimeType: "audio/webm; codecs=opus" })).catch(() => {
                window.alert("no microphone found.");
                return undefined;
            });
    } catch {
        return undefined;
    }
}

const recorder = await createRecorder();

// export const wtContainer: { wt: WebTransport | null } = { wt: null };

// function setWt() {
//     if (!window.isSecureContext || !recorder) {
//         return;
//     }
//     const wt = new WebTransport("https://core.poscon.com/?access_token=NOT_USED_YET", {
//         requireUnreliable: true,
//         congestionControl: "low-latency",
//     });
//     wt.closed
//         .then((reason) => {
//             console.log("webtransport closed with reason: ", reason);
//             setWt();
//         })
//         .catch(console.warn);

//     wtContainer.wt = wt;
// }

// setWt();

export async function sendVoiceData(chunks: Blob[], data: object) {
    if (window.__TAURI__) {
        await invoke("record_transcription", {
            metadata: JSON.stringify(data),
            audio_chunks: await Promise.all(chunks.map(async (c) => new Uint8Array(await c.arrayBuffer())))
        });
    }
    // // one stream for each recording and its metadata
    // const wt = wtContainer.wt;
    // if (!wt) {
    //     return;
    // }
    // await wt.ready;
    // const stream = await wt.createUnidirectionalStream();
    // const writer = stream.getWriter();
    // await writer.ready;
    // try {
    //     await writer.write(encode("recording"));
    //     await writer.write(encode("receive"));
    //     // metadata comes first, either a MsgPack string of JSON or a MsgPack key-value Map directly
    //     await writer.write(encode(JSON.stringify(data)));
    //     for await (const chunk of chunks) {
    //         // then loop until end of recording
    //         await writer.ready; // in-case we are too fast
    //         await writer.write(encode(new Uint8Array(await chunk.arrayBuffer())));
    //     }
    //     writer.write(encode(null));
    // } catch {
    //     // ignore
    // }
    // await writer.close();
    // console.log("Recording sent");
}

if (window.isSecureContext && recorder) {
    const listener = (e: BlobEvent) => {
        if (data.isRecording) {
            data.chunks.push(e.data);
        }
    };

    window.addEventListener(
        "keydown",
        (e) => {
            const store = data.store;
            if (!store) {
                return;
            }
            const connection = connectionSelector(store.getState());
            if (((connection?.isActive && connection?.sectorId) || import.meta.env.DEV) && e.key === "Shift") {
                if (!data.isRecording) {
                    data.isRecording = true;
                    data.chunks = [];
                    console.log("starting recording");
                    recorder.addEventListener("dataavailable", listener);
                    recorder.start(100);
                }
            }
        },
        { capture: true }
    );

    window.addEventListener(
        "keyup",
        async (e) => {
            const store = data.store;
            if (!store) {
                return;
            }
            const url = data.transcribeVoiceBaseUrl;
            const connection = connectionSelector(store.getState());
            const sectorId = connection?.sectorId;
            if (connection && sectorId && e.key === "Shift" && data.isRecording && url) {
                console.log("stopped recording");
                recorder.stop();
                recorder.removeEventListener("dataavailable", listener);

                data.isRecording = false;
                const chunks = data.chunks;
                data.chunks = [];
                if (chunks.length <= 5) {
                    return;
                }
                const blob = new Blob(chunks, { type: "audio/webm; codecs=opus" });
                const file = new File([blob], "recording.webm", { type: "audio/webm; codecs=opus" });
                const formData = new FormData();
                formData.append("audio_file", file);
                formData.append("export_to_file", 'true');
                formData.append("sector_id", sectorId);

                const artccId = typedSplitString(sectorId, ".")[0];

                const json = await fetch(`${url}/transcribe-audio/`, {
                    method: "POST",
                    signal: AbortSignal.timeout(TRANSCRIBE_TIMEOUT),
                    body: formData,
                })
                    .then((r): Promise<TranscribeData> => r.json())
                    .catch((e) => {
                        console.warn(e);
                        return null;
                    });
                if (!json) {
                    return;
                }
                console.log(`received command: `, json);
                const { callsign = null, text = "", audio_file_path = null, ...instruction } = json;
                if (instruction.ignore === true) {
                    return;
                }
                const droneCommandUrl = `${ERAM_SERVER_URL}/${artccId}/api/droneInstruction`;
                const commandResponse = await fetch(droneCommandUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    signal: AbortSignal.timeout(10000), // 10 secs
                    body: JSON.stringify({ instruction, callsign, sectorId: connection?.sectorId }),
                }).catch((e) => e);

                if (!commandResponse?.ok) {
                    const statusText = commandResponse?.statusText;
                    const errorText = await commandResponse?.text();
                    console.warn("Failed to execute drone command: ", { statusText, errorText });

                    store?.dispatch(
                        setMraMessage([
                            [`COULD NOT EXECUTE COMMAND FOR CALLSIGN ${callsign?.toUpperCase()}`, text.toUpperCase(), `STATUS: ${statusText}`, errorText ?? ""],
                        ])
                    );
                    if (text && callsign && Object.keys(instruction).length > 0) {
                        void sendVoiceData(chunks, { callsign, command: instruction, instructionText: text, sectorId: connection?.sectorId, userId: connection?.userId, statusText, errorText });
                    }
                    return;
                }

                if (!shouldIgnoreResponsePlayback(instruction) && audio_file_path) {
                    await playAudioFile(audio_file_path);
                }

                store?.dispatch(
                    setMraMessage([[`RECOGNIZED CALLSIGN: ${callsign?.toUpperCase()}`, text.toUpperCase()]])
                );
            }
        },
        { capture: true }
    );
}

type TTSResponseData = {
    audio_file_path: string;
    converted_text: string;
}

export async function playbackText(action: Record<string, string>) {
    const url = data.transcribeVoiceBaseUrl;
    if (!url) {
        return;
    }

    const ttsData = await fetch(`${url}/tts/`, {
        method: "POST",
        signal: AbortSignal.timeout(10000),
        body: new URLSearchParams(action),
    })
        .then((r): Promise<TTSResponseData> => r.json())
        .catch((e) => {
            console.warn(e);
            return null;
        });
    if (!ttsData) {
        return;
    }
    const { audio_file_path } = ttsData;

    if (audio_file_path) {
        await playAudioFile(audio_file_path);
    }
}

async function playAudioFile(filepath: string) {
    const url = data.transcribeVoiceBaseUrl;
    if (!url) {
        return;
    }

    const responseVoiceDownloadPath = `${url}${filepath.replace(/^\/voice/, "")}`;
    try {
        const blob = await fetch(responseVoiceDownloadPath).then((r) => r.blob());
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.play();
    } catch (e) {
        console.error(e);
    }
}
